import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { WizardContextType, WizardData, WizardSession } from '../types/wizard';
import api from '../services/api';

// Initial state
const initialWizardData: WizardData = {
  name: '',
  email: '',
  phone: '',
  specialties: [],
  bio: '',
  services_offered: [],
  treatment_categories: [],
  virtual_consultations: false,
  insurance_accepted: [],
  years_of_experience: 0,
  education: [],
  certifications: [],
  professional_affiliations: [],
  languages_spoken: [],
};

interface WizardState {
  currentStep: number;
  sessionId: string | null;
  wizardData: WizardData;
  isLoading: boolean;
  errors: Record<string, string>;
}

const initialState: WizardState = {
  currentStep: 1,
  sessionId: null,
  wizardData: initialWizardData,
  isLoading: false,
  errors: {},
};

// Action types
type WizardAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'SET_SESSION_ID'; payload: string }
  | { type: 'UPDATE_DATA'; payload: Partial<WizardData> }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERRORS'; payload: Record<string, string> }
  | { type: 'RESET' };

// Reducer
const wizardReducer = (state: WizardState, action: WizardAction): WizardState => {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.payload };
    case 'UPDATE_DATA':
      return { ...state, wizardData: { ...state.wizardData, ...action.payload } };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERRORS':
      return { ...state, errors: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

// Context
const WizardContext = createContext<WizardContextType | undefined>(undefined);

// Provider component
export const WizardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(wizardReducer, initialState);

  const goToStep = useCallback((step: number) => {
    dispatch({ type: 'SET_STEP', payload: step });
  }, []);

  const nextStep = useCallback(() => {
    if (state.currentStep < 5) {
      dispatch({ type: 'SET_STEP', payload: state.currentStep + 1 });
    }
  }, [state.currentStep]);

  const previousStep = useCallback(() => {
    if (state.currentStep > 1) {
      dispatch({ type: 'SET_STEP', payload: state.currentStep - 1 });
    }
  }, [state.currentStep]);

  // Update step data locally only (no API calls)
  const updateStepData = useCallback(async (stepData: Partial<WizardData>) => {
    dispatch({ type: 'SET_ERRORS', payload: {} });
    dispatch({ type: 'UPDATE_DATA', payload: stepData });
  }, []);

  // Save all wizard data after step 3.
  // extraData lets callers include data just passed to updateStepData (not yet visible in this
  // callback's closure). Resolves to the new session id, or null on failure.
  const saveWizardData = useCallback(async (extraData: Partial<WizardData> = {}) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERRORS', payload: {} });

    // Save all wizard data at once
    const allData: WizardData = { ...state.wizardData, ...extraData };

    try {
      // Check email availability first
      const emailCheck = await api.post('/api/wizard/check-email', {
        email: allData.email,
      });

      if (!emailCheck.data.available) {
        dispatch({ type: 'SET_ERRORS', payload: { email: 'This email is already registered' } });
        return null;
      }

      // Start wizard session
      const sessionResponse = await api.post<WizardSession>('/api/wizard/start', {
        email: allData.email,
      });
      const sessionId = sessionResponse.data.session_id;
      dispatch({ type: 'SET_SESSION_ID', payload: sessionId });

      // Save using the backend wizard's step layout and field names
      // Basic information
      await api.put(`/api/wizard/session/${sessionId}/data/1`, {
        data: {
          full_name: allData.name,
          email: allData.email,
          phone: allData.phone,
          profile_photo: allData.profile_photo,
        },
      });

      // Professional details
      await api.put(`/api/wizard/session/${sessionId}/data/2`, {
        data: {
          specialties: allData.specialties,
          years_of_experience: allData.years_of_experience,
          bio: allData.bio,
        },
      });

      // Education & certifications
      await api.put(`/api/wizard/session/${sessionId}/data/3`, {
        data: {
          education: allData.education,
          certifications_list: allData.certifications,
          affiliations: allData.professional_affiliations,
          languages_spoken: allData.languages_spoken,
        },
      });

      // Services
      await api.put(`/api/wizard/session/${sessionId}/data/4`, {
        data: {
          services: allData.services_offered,
          treatment_categories: allData.treatment_categories,
          virtual_consultations: allData.virtual_consultations,
          insurance_accepted: allData.insurance_accepted,
        },
      });

      return sessionId;
    } catch (error: unknown) {
      // Handle validation errors from FastAPI
      let errorMessage = 'An error occurred';

      const err: any = error as any;
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;

        if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        } else {
          errorMessage = detail;
        }
      }

      dispatch({ type: 'SET_ERRORS', payload: { general: errorMessage } });
      return null;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.wizardData]);

  const uploadPhoto = useCallback(
    async (file: File): Promise<string> => {
      if (!state.sessionId) {
        throw new Error('No wizard session');
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await api.post<{ url: string }>('/api/wizard/upload-photo', formData, {
          params: { session_id: state.sessionId },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data.url;
      } catch (error) {
        throw error;
      }
    },
    [state.sessionId]
  );

  const resetWizard = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const contextValue: WizardContextType = {
    ...state,
    goToStep,
    nextStep,
    previousStep,
    updateStepData,
    saveWizardData,
    uploadPhoto,
    resetWizard,
  };

  return <WizardContext.Provider value={contextValue}>{children}</WizardContext.Provider>;
};

// Hook to use wizard context
export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};
